import { baseApi } from './baseApi';

export interface NonTPMConnectionAttempt {
  id: string;
  device_id: string;
  mac_address: string;
  ip_address?: string;
  hardware_model?: string;
  firmware_version?: string;
  client_version?: string;
  tpm_error_message?: string;
  first_attempt_at: string;
  last_attempt_at: string;
  attempt_count: number;
  reviewed: boolean;
  reviewed_at?: string;
  reviewed_by?: string;
  review_decision?: string;
  temporary_access_granted: boolean;
  temporary_access_expires_at?: string;
  temporary_access_granted_by?: string;
  is_access_expired: boolean;
  time_until_expiry: number;
}

export interface GrantAccessRequest {
  duration_hours: number;
  reason: string;
  auto_create_device?: boolean;
}

export interface GrantAccessResponse {
  success: boolean;
  message: string;
  device_id: string;
  expires_at: string;
  access_duration_hours: number;
}

export interface NonTPMStatistics {
  total_attempts: number;
  unique_devices: number;
  unreviewed: number;
  active_access: number;
  expired_access: number;
  requires_attention: number;
}

interface ConnectionAttemptsArgs {
  unreviewed_only?: boolean;
  active_access_only?: boolean;
  limit?: number;
}

export const nonTpmApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNonTpmConnectionAttempts: builder.query<NonTPMConnectionAttempt[], ConnectionAttemptsArgs | void>({
      query: (args) => {
        const params: Record<string, string | number | boolean> = {};
        if (args?.unreviewed_only) params.unreviewed_only = true;
        if (args?.active_access_only) params.active_access_only = true;
        if (args?.limit) params.limit = args.limit;

        return {
          url: '/non-tpm-devices/connection-attempts',
          params,
        };
      },
      providesTags: [{ type: 'NonTpmDevice', id: 'LIST' }],
    }),

    getNonTpmStatistics: builder.query<NonTPMStatistics, void>({
      query: () => '/non-tpm-devices/statistics',
      providesTags: [{ type: 'NonTpmDevice', id: 'STATS' }],
    }),

    grantNonTpmAccess: builder.mutation<GrantAccessResponse, { deviceId: string; request: GrantAccessRequest }>({
      query: ({ deviceId, request }) => ({
        url: `/non-tpm-devices/${deviceId}/grant-access`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: [
        { type: 'NonTpmDevice', id: 'LIST' },
        { type: 'NonTpmDevice', id: 'STATS' },
      ],
    }),

    revokeNonTpmAccess: builder.mutation<void, string>({
      query: (deviceId) => ({
        url: `/non-tpm-devices/${deviceId}/revoke-access`,
        method: 'POST',
      }),
      invalidatesTags: [
        { type: 'NonTpmDevice', id: 'LIST' },
        { type: 'NonTpmDevice', id: 'STATS' },
      ],
    }),
  }),
});

export const {
  useGetNonTpmConnectionAttemptsQuery,
  useGetNonTpmStatisticsQuery,
  useGrantNonTpmAccessMutation,
  useRevokeNonTpmAccessMutation,
} = nonTpmApi;
